import type { LocalFile } from '@frontend/api-types';
import { makeLocalFileList } from '@frontend/forms';
import type { DropzoneProps, ModalProps } from '@frontend/web-react';
import { Dropzone, Group, Icon, Modal, Text, useDragState, YStack } from '@frontend/web-react';

export type DragAndDropModalProps = Partial<DropzoneProps & ModalProps> & {
  /**
   * The contents to display when the file type is one of the accepted types from the `accept` prop
   */
  acceptContents?: React.ReactNode;
  /**
   * Prevents the modal from opening
   */
  disabled?: boolean;
  /**
   * Indicates if the file limit has been reached
   */
  fileLimitReached?: boolean;
  /**
   * The contents to display when the file limit has been reached
   */
  fileLimitReachedContent?: React.ReactNode;
  /**
   * The contents to display when nothing has been dragged into the dropzone
   */
  idleContents?: React.ReactNode;
  /**
   * Passes the dropped files to the passed in callback
   */
  onFilesDropped: (droppedFiles: LocalFile[]) => void;
  /**
   * The contents to display when the file type isn't one of the accepted types from the `accept` prop
   */
  rejectContents?: React.ReactNode;
};

const defaultAccept = {
  IMAGE_MIME_TYPE: [],
  PDF_MIME_TYPE: [],
};

export const DragAndDropModal = ({
  accept = defaultAccept,
  acceptContents = 'Drag and drop file(s) here',
  disabled,
  fileLimitReached,
  fileLimitReachedContent,
  idleContents = 'Drag and drop file(s) here',
  onClose = () => {},
  onFilesDropped,
  rejectContents = 'This file type is not supported',
}: DragAndDropModalProps) => {
  const isDragging = useDragState();

  const onDrop = (newFiles: File[]) => {
    if (!fileLimitReached) {
      const localFileList = makeLocalFileList(newFiles);
      onFilesDropped(localFileList);
    }
  };

  if (disabled) {
    return null;
  }

  return (
    <Modal isOpen={isDragging} onClose={onClose} size="100%" title="">
      <Dropzone
        accept={accept}
        classNames={{
          inner: 'h-full',
          root: 'h-[75vh] rounded-lg border-0',
        }}
        onDrop={onDrop}
        style={{
          backgroundImage:
            "url(\"data:image/svg+xml,%3csvg width='100%25' height='100%25' xmlns='http://www.w3.org/2000/svg'%3e%3crect width='100%25' height='100%25' fill='none' rx='5' ry='5' stroke='%23DCDCDC' stroke-width='3' stroke-dasharray='20' stroke-dashoffset='0' stroke-linecap='square'/%3e%3c/svg%3e\")",
        }}
      >
        <Group className="flex size-full items-center justify-center" gap="xl" justify="center">
          {fileLimitReached ? (
            fileLimitReachedContent ?? <Text>{"You've reached the maximum number of files."}</Text>
          ) : (
            <YStack className="text-center" gap="md">
              <Icon className="mx-auto" name="IconArrowsMove" />
              <Dropzone.Accept>{acceptContents}</Dropzone.Accept>
              <Dropzone.Reject>{rejectContents}</Dropzone.Reject>
              <Dropzone.Idle>{idleContents}</Dropzone.Idle>
            </YStack>
          )}
        </Group>
      </Dropzone>
    </Modal>
  );
};
